import { useMemo } from 'react';
import InputConfig from '../../../../Components/InputConfig';
import DropDownSingle from '../../../../Components/DropDownSingle';
import GeneralIconButton from '../../../../Components/GeneralIconButton';
import SwitchInput from '../../../../Components/SwitchInput';
import CustomHeading from '../../../../Components/CustomHeading';
//mui imports====
import { Grid } from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import AddCircleOutlineOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import useComonStyles from '../../../../commonStyles';

import { toast } from 'react-toastify';

const CustomParentToChildNetworkMapping = ({
	customParentToChildMappingConfig,
	updateCustomParentToChildMappingConfig,
	transformedGamIdsConfig
}) => {
	const commonStyleClasses = useComonStyles();
	const mappingObject = customParentToChildMappingConfig?.mapping || {};

	// Extract GAM IDs that are available
	const availableGamIds = transformedGamIdsConfig.map(({ value }) => value);
	const filteredGamNetworkIds = availableGamIds.filter((item) => item !== '' && /^\d+$/.test(item));

	/**
	 * Filters out GAM IDs that are not already mapped.
	 * @returns {string[]} List of available GAM IDs.
	 */
	const filteredCustomMapping = useMemo(() => {
		const existingGamIds = Object.keys(mappingObject);
		return filteredGamNetworkIds.filter((gamId) => !existingGamIds.includes(gamId));
	}, [mappingObject, filteredGamNetworkIds]);

	/**
	 * Enables or disables custom parent-to-child mapping.
	 * @param {boolean} value - Toggle state.
	 */
	const toggleCustomParentToChildMapping = (value) => {
		const clonedConfig = structuredClone(customParentToChildMappingConfig) || {};
		clonedConfig.enabled = value;
		updateCustomParentToChildMappingConfig(clonedConfig);
	};

	/**
	 * Updates the child MCM ID for a given parent GAM ID.
	 * @param {string} gamId - Parent GAM ID.
	 * @param {string} newValue - New child MCM ID.
	 */
	const updateChildMcmIdValue = (gamId, newValue) => {
		if (newValue === null || newValue === undefined) {
			return;
		}

		const clonedConfig = structuredClone(customParentToChildMappingConfig) || {};
		clonedConfig.mapping[gamId] = newValue;
		updateCustomParentToChildMappingConfig(clonedConfig);
	};

	/**
	 * Updates the parent GAM ID mapping.
	 * @param {string} oldGamId - Existing GAM ID.
	 * @param {string} newGamId - New GAM ID.
	 */
	const updateCustomParentGamIdKey = (oldGamId, newGamId) => {
		const clonedConfig = structuredClone(customParentToChildMappingConfig) || {};
		if (!clonedConfig.mapping) return;

		if (clonedConfig.mapping[newGamId] !== undefined) {
			toast.error(`${newGamId} mapping already exists`, { position: 'bottom-right' });
			return;
		}

		if (clonedConfig.mapping[oldGamId] === undefined) {
			toast.error(`${oldGamId} does not exist`, { position: 'bottom-right' });
			return;
		}

		clonedConfig.mapping[newGamId] = clonedConfig.mapping[oldGamId];
		delete clonedConfig.mapping[oldGamId];

		updateCustomParentToChildMappingConfig(clonedConfig);
	};
	/**
	 * Deletes a custom mapping by key.
	 * @param {string} key - GAM ID to remove.
	 */
	const deleteCustomParentGamIdMapping = (key) => {
		if (!key) return;

		const clonedConfig = structuredClone(customParentToChildMappingConfig) || {};
		if (!clonedConfig.mapping || clonedConfig.mapping[key] == null) return;

		delete clonedConfig.mapping[key];

		updateCustomParentToChildMappingConfig(clonedConfig);
	};
	/**
	 * Adds a new custom mapping if available.
	 */
	const AddCustomParentToChildMapping = () => {
		if (filteredCustomMapping.length === 0) {
			toast.error(`No Gam Id available for mapping`, { position: 'bottom-right' });
			return;
		}

		const newKey = filteredCustomMapping[0];

		const clonedConfig = structuredClone(customParentToChildMappingConfig) || {};
		if (!clonedConfig.mapping) {
			clonedConfig.mapping = {};
		}

		if (clonedConfig.mapping[newKey] !== undefined) {
			toast.error(`${newKey} mapping already exists`, { position: 'bottom-right' });
			return;
		}

		clonedConfig.mapping[newKey] = '';

		updateCustomParentToChildMappingConfig(clonedConfig);
	};

	return (
		<>
			<Grid item xs={12} display={'flex'}>
				<CustomHeading
					heading={'Custom Parent To Child Mapping'}
					name={`CUSTOM_PARENT_TO_CHILD_MAPPING_HEADING`}
					variantType="h6"
					headingStyles={{ marginTop: '1rem' }}
				/>

				<GeneralIconButton
					color="primary"
					fontSize="medium"
					className={commonStyleClasses.icon}
					style={{ marginLeft: '1rem', float: 'left' }}
					name={'CUSTOM_PARENT_TO_CHILD_MAPPING_ADD_BTN'}
					onClick={AddCustomParentToChildMapping}
				>
					<AddCircleOutlineOutlined />
				</GeneralIconButton>
			</Grid>

			<Grid item xs={12}>
				<SwitchInput
					keyData={'customParentToChildNetworkMapping.enabled'}
					label="Enabled"
					isObjectEditor={false}
					directValue={customParentToChildMappingConfig?.enabled}
					updateDirectValue={toggleCustomParentToChildMapping}
					containerStyles={{ width: '10%' }}
				/>
			</Grid>

			<Grid container spacing={2} marginTop={'1rem'} marginLeft={'0.4rem'}>
				{Object.keys(mappingObject).map((gamId) => (
					<>
						<Grid item xs={2} marginTop={'0.6rem'}>
							<DropDownSingle
								label={'Parent Gam ID'}
								keyData={'customParentToChildNetworkMapping.mapping'}
								isObjectEditor={false}
								list={filteredGamNetworkIds.map((id) => ({ label: id, value: id }))}
								directValue={gamId}
								updateDirectValue={(newValue) => updateCustomParentGamIdKey(gamId, newValue)}
							/>
						</Grid>
						<Grid item xs={2}>
							<InputConfig
								keyData={'customParentToChildNetworkMapping.mapping'}
								label={'Child MCM ID'}
								isObjectEditor={false}
								directValue={customParentToChildMappingConfig?.mapping?.[gamId]}
								updateDirectValue={(value) => updateChildMcmIdValue(gamId, value)}
								containerStyles={{ marginTop: '0.4rem' }}
							/>
						</Grid>
						<Grid item xs={2}>
							<GeneralIconButton
								color="primary"
								fontSize="medium"
								className={commonStyleClasses.icon}
								style={{ float: 'left', marginTop: '4.3rem' }}
								name="CUSTOM_PARENT_TO_CHILD_MAPPING_DELETE_BTN"
								onClick={() => deleteCustomParentGamIdMapping(gamId)}
							>
								<DeleteOutlined />
							</GeneralIconButton>
						</Grid>
					</>
				))}
			</Grid>
		</>
	);
};

export default CustomParentToChildNetworkMapping;
